import React from 'react';

import {addOrReplaceAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {findAndAssertSingleStepDataFrameId} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import getPersistedVisualizationConfig from 'analysis-tool/ui/charting/getPersistedVisualizationConfig';
import VisualizationStepEditor from 'analysis-tool/ui/charting/VisualizationStepEditor';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {VisualizationConfig} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {getTableLineageDescendantSteps} from 'pipelines/StepLineageUtils';
import {withLoadingScreen} from 'util/Promises';

import getDefaultMainViewItemForStep from '../getDefaultMainViewItemForStep';
import {AnalysisMainViewItemType, MainViewComponentProps} from './MainViewTypes';

export default function CreateVisualizationView({
  analysis,
  clientStateId,
  onCloseStepInitializationView,
  step: datasetViewStep,
  selectedItem,
}: MainViewComponentProps<AnalysisMainViewItemType.VISUALIZATION_CONFIGURATION_FOR_DATASET_VIEW>): JSX.Element | null {
  const datasetViewSteps = getTableLineageDescendantSteps(analysis, datasetViewStep);
  const terminalStep = datasetViewSteps[datasetViewSteps.length - 1];
  const lastSucceededStep = datasetViewSteps.findLast(
    (step) => step.statusCode === PipelineStepStatusCode.SUCCEEDED
  )!;
  const inputDataFrameId = findAndAssertSingleStepDataFrameId(
    lastSucceededStep,
    SINGLE_DATASET_OUTPUT_STEP_DATA_NAME
  );

  async function handleSaveChart(
    visualizationConfig: VisualizationConfig,
    visualizationName: string
  ): Promise<void> {
    const newStep = await withLoadingScreen(
      addOrReplaceAndRunAnalysisStep(analysis.id, {
        stepType: PipelineStepType.VISUALIZATION,
        stepVersion: 0,
        stepConfig: {
          visualizationConfig: getPersistedVisualizationConfig(visualizationConfig),
          visualizationName,
        },
        stepInputParents: {
          [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [
            {stepId: terminalStep.id, stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME},
          ],
        },
        stepInput: {inputDataFrameId},
      }),
      'Saving view...'
    );

    onCloseStepInitializationView(getDefaultMainViewItemForStep(newStep));
  }

  return (
    <VisualizationStepEditor
      analysis={analysis}
      clientStateId={clientStateId}
      inputDataFrameId={inputDataFrameId}
      handleSaveChart={handleSaveChart}
      viewSteps={datasetViewSteps}
      initialVisualizationConfig={selectedItem.initialStepConfig?.visualizationConfig}
      initialVisualizationName={selectedItem.initialStepConfig?.visualizationName}
    />
  );
}
